'use client'
import React, { useLayoutEffect, useRef } from 'react';

const VortexStar = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const numStars = 80;

    function createStar() {
      const star = document.createElement('div');
      star.classList.add('star');

      // Set a random size (e.g., between 1 and 3px)
      const size = Math.random() * 1 + 1;
      star.style.width = `${size}px`;
      star.style.height = `${size}px`;

      // Get container dimensions and compute center coordinates
      const width = container!.clientWidth || window.innerWidth;
      const height = container!.clientHeight || window.innerHeight;
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.min(width, height) / 1.4;

      // Choose a random angle and position the star on the outer edge
      const angle = Math.random() * Math.PI * 2;
      const initialX = centerX + radius * Math.cos(angle);
      const initialY = centerY + radius * Math.sin(angle);

      // Compute translation vector from the star's initial position to the center
      const dx = centerX - initialX;
      const dy = centerY - initialY;

      // Position the star at its start point
      star.style.position = 'absolute';
      star.style.left = `${initialX}px`;
      star.style.top = `${initialY}px`;

      // Animation duration is randomized between 3 and 6 seconds.
      const duration = Math.random() * 3000 + 9000; // in ms
      star.style.animation = `moveStar ${duration}ms linear infinite`;
      star.style.animationDelay = `-${Math.random() * duration}ms`;

      // Set custom properties so the CSS animation can use the computed vector
      star.style.setProperty('--dx', `${dx}px`);
      star.style.setProperty('--dy', `${dy}px`);

      container!.appendChild(star);
    }

    // Clear any existing stars and create new ones.
    container.innerHTML = '';
    for (let i = 0; i < numStars; i++) {
      createStar();
    }

    // Recreate the star field on window resize.
    function handleResize() {
      container!.innerHTML = '';
      for (let i = 0; i < numStars; i++) {
        createStar();
      }
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    // The added class "rotate-field" applies a subtle rotation to the entire star field.
    <div
      ref={containerRef}
      className="absolute z-5 inset-0 pointer-events-none rotate-field"
    />
  );
};

export default VortexStar; 